import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { useFormik } from 'formik';
import { deleteRequest, downloadBlobGet, fetchTextGet, getRequest, postRequest } from '../../../api/ApiController';
import { AddIcon, CopyIcon, DeleteIcon, EditIcon, EditRowIcon, ExportIcon, FavoriteIcon, ForwardIcon, ForwardToLastIcon, PriceIcon, PrintIcon, RefreshIcon, SearchIcon, VerticalLineIcon, BackToFirstIcon, BackIcon } from '../../svg/svgIcons';
import { Dialog } from 'primereact/dialog';
import AuthContext from '../../../components/AuthContext';
import { LocaleContext } from '../../../components/LocaleContext';
import UtilContext from '../../../components/UtilContext';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { Dropdown } from 'primereact/dropdown';
import { Avatar } from 'primereact/avatar';
import { DateTime } from 'luxon';
import { getCurrency } from '../../../utils/moduleSettings';
import { formatCurrency } from '../../../utils/currencyUtils';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import { applyUserMaskRowToTabulator, cleanSorters, safeParse } from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../../utils/tabulatorLayoutUtils';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { dateUserHeaderFilter } from '../../../utils/dateUserHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth, TABULATOR_LIST_ID_MIN_WIDTH } from '../../../utils/tabulatorColumnMinWidth';
import PrintOptionsDialog from '../../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../../utils/exportExcelNaming';
import { MdSearch, MdSearchOff } from 'react-icons/md';

const PAGE_ID = 'OtkCompanyStatisticsAdd';

const DEFAULT_COLUMNS_VISIBLE_STATS = Object.freeze({
  id: true,
  period: true,
  monthly_revenue: true,
  monthly_claim_costs: true,
  sync_claim_costs: true,
  orders_count: true,
  items_count: true,
  created_at: true,
  updated_at: true,
});

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE_STATS };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE_STATS).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE_STATS };
  return merged;
}

function normalizeStatsColumnVisibilityForSave(stateObj) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_STATS)) out[field] = stateObj?.[field] !== false;
  return out;
}

function columnsFromTabulatorForStandardMaskSave(raw) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_STATS)) out[field] = raw?.[field] !== false;
  return out;
}

function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try { table.setHeaderFilterValue?.(field, '', true); } catch (_) {}
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE_STATS)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function countVisibleDataColumns(vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(DEFAULT_COLUMNS_VISIBLE_STATS).filter((k) => vis[k] !== false).length;
}

const normalizeText = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function mapRowToUpdateFormValues(row) {
  if (!row) return null;
  return {
    id: row.id,
    month: Number(row.month),
    year: Number(row.year),
    monthly_revenue: (row.monthly_revenue === null || row.monthly_revenue === undefined) ? '' : String(row.monthly_revenue).replace(',', '.'),
    monthly_claim_costs: (row.monthly_claim_costs === null || row.monthly_claim_costs === undefined) ? '' : String(row.monthly_claim_costs).replace(',', '.'),
    orders_count: row.orders_count ?? '',
    items_count: row.items_count ?? '',
    sync_claim_costs: !!row.sync_claim_costs,
  };
}

export default function OtkCompanyStatisticsAdd({ otk_company, type }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const {
    setDialogText,
    setVisible,
    openFavoriteDialog,
    toast,
    setFastView,
    setUserMaskFilter,
    setStandardMaskFilter,
    openChosenNotificationPreferences,
    getDateFormat,
    formatDate,
    getUserInitials,
    getUserColor,
  } = useContext(UtilContext);
  const { user } = useContext(AuthContext);
  const { t } = useContext(LocaleContext);

  const [closeTab, setCloseTab] = useState(false);
  const ref = useRef(null);
  const overlayRef = useRef(null);
  const isRestoringRef = useRef(false);
  const submittedSearchValueRef = useRef('');

  const [formType] = useState(type);
  const [showOtkStatisticsAdd, setShowOtkStatisticsAdd] = useState(false);

  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE_STATS });
  const [maskVisible, setMaskVisible] = useState(false);

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);

  const [selectedStatistics, setSelectedStatistics] = useState(null);
  const [visibleCompanyStatistics, setVisibleCompanyStatistics] = useState(false);

  const [expandedKeys, setExpandedKeys] = useState({});
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchIconColor, setSearchIconColor] = useState('');
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);

  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === PAGE_ID);
    if (!row) return false;
    const attributes = safeParse(row.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const cols = safeParse(row.columns, {});
    const merged = mergeVisibility(cols && typeof cols === 'object' ? cols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const statisticsRows = useMemo(() => {
    const list = (state.company_statistics || []).filter((s) => Number(s.company_id) === Number(otk_company?.id));
    return [...list].sort((a, b) => {
      const yd = (Number(b.year) || 0) - (Number(a.year) || 0);
      if (yd !== 0) return yd;
      return (Number(b.month) || 0) - (Number(a.month) || 0);
    });
  }, [state.company_statistics, otk_company?.id]);

  const navigateToStatId = useCallback((id) => {
    const table = getTable();
    if (!table || id == null) return;
    try {
      const rows = table.getRows?.() ?? [];
      const row = rows.find((r) => Number(r.getData?.()?.id) === Number(id));
      if (row) {
        row.select?.();
        table.scrollToRow?.(row, 'middle', false);
      }
    } catch (_) {}
  }, [getTable]);

  const scrollToFirstStat = useCallback(() => {
    if (!statisticsRows.length) return;
    navigateToStatId(statisticsRows[0].id);
  }, [statisticsRows, navigateToStatId]);

  const scrollToLastStat = useCallback(() => {
    if (!statisticsRows.length) return;
    navigateToStatId(statisticsRows[statisticsRows.length - 1].id);
  }, [statisticsRows, navigateToStatId]);

  const scrollUpStat = useCallback(() => {
    const curId = selectedStatistics?.id;
    const idx = statisticsRows.findIndex((s) => Number(s.id) === Number(curId));
    if (idx > 0) navigateToStatId(statisticsRows[idx - 1].id);
    else if (idx < 0 && statisticsRows.length) navigateToStatId(statisticsRows[0].id);
  }, [statisticsRows, selectedStatistics?.id, navigateToStatId]);

  const scrollDownStat = useCallback(() => {
    const curId = selectedStatistics?.id;
    const idx = statisticsRows.findIndex((s) => Number(s.id) === Number(curId));
    if (idx >= 0 && idx < statisticsRows.length - 1) navigateToStatId(statisticsRows[idx + 1].id);
    else if (idx < 0 && statisticsRows.length) navigateToStatId(statisticsRows[0].id);
  }, [statisticsRows, selectedStatistics?.id, navigateToStatId]);

  const reloadStatisticsAndCompanies = useCallback(() => {
    getRequest('/api/company_statistics').then((response) => {
      if (response?.data) dispatch({ type: 'load_company_statistics', newCompanyStatistics: response.data });
    }).catch(() => {});
    getRequest('/api/companies').then((response) => {
      if (response?.data) dispatch({ type: 'load_companies', newCompanies: response.data });
    }).catch(() => {});
  }, [dispatch]);

  const applyToolbarSearchRowFilter = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const needle = normalizeText(String(submittedSearchValueRef.current ?? '').trim());
    if (needle === '') {
      table.clearFilter(true);
      return;
    }
    table.setFilter((data) => normalizeText(JSON.stringify(data)).includes(needle));
  }, [getTable]);

  const validate = (values) => {
    const errors = {};
    return errors;
  };

  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
    label: t(`OtkCompanyStatisticsPeriodList.Month.${i + 1}`),
    val: i + 1,
  })), [t]);

  const formatDecimalForSubmit = (v) => (v === null || v === undefined || v === '') ? '' : String(v).trim().replace(',', '.');
  const formatDecimalForDisplay = (v) => (v === null || v === undefined) ? '' : String(v).replace(',', '.');

  const years = Array.from({ length: 36 }, (_, i) => {
    const y = 2000 + i;
    return { label: String(y), val: y };
  });

  const formik = useFormik({
    initialValues: {
      month: '', year: '', monthly_revenue: '', monthly_claim_costs: '', orders_count: '', items_count: '', sync_claim_costs: true,
    },
    enableReinitialize: true,
    validate,
    onSubmit: (values) => {
      const formData = new FormData();
      for (const value of Object.keys(values)) {
        if (value === 'sync_claim_costs') formData.append(value, values[value] ? 1 : 0);
        else if (value === 'monthly_revenue' || value === 'monthly_claim_costs') formData.append(value, formatDecimalForSubmit(values[value]));
        else formData.append(value, values[value] ?? '');
      }
      formData.append('company_id', otk_company.id);
      postRequest('/api/company_statistics', formData, true)
        .then(() => {
          getRequest('/api/company_statistics').then((response) => {
            dispatch({ type: 'load_company_statistics', newCompanyStatistics: response.data });
            if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
            setShowOtkStatisticsAdd(false);
            formik.resetForm();
            toast?.current?.show({
              severity: 'info',
              summary: t('BusinessPartnerList.Toast.DeletedSummary'),
              life: 3000,
              detail: t('Common.Toast.RecordSavedDetail'),
            });
          });
          getRequest('/api/companies').then((response) => {
            dispatch({ type: 'load_companies', newCompanies: response.data });
          });
        })
        .catch(() => {
          if (!navigator.onLine) {
            alert(t('ServiceTask.OfflineQueuedAlert'));
          } else {
            alert(t('Common.Toast.ActionFailedDetail'));
          }
        });
    },
  });

  const formikUpdate = useFormik({
    initialValues: selectedStatistics == null ? {
      month: '', year: '', monthly_revenue: '', monthly_claim_costs: '', orders_count: '', items_count: '', sync_claim_costs: true,
    } : {
      id: selectedStatistics.id,
      month: Number(selectedStatistics.month),
      year: Number(selectedStatistics.year),
      monthly_revenue: formatDecimalForDisplay(selectedStatistics.monthly_revenue),
      monthly_claim_costs: formatDecimalForDisplay(selectedStatistics.monthly_claim_costs),
      orders_count: selectedStatistics.orders_count ?? '',
      items_count: selectedStatistics.items_count ?? '',
      sync_claim_costs: !!selectedStatistics.sync_claim_costs,
    },
    enableReinitialize: true,
    validate,
    onSubmit: (values) => {
      const formData = new FormData();
      const v = { ...values };
      delete v.files;
      for (const key of Object.keys(v)) {
        if (key === 'sync_claim_costs') formData.append(key, v[key] ? 1 : 0);
        else if (key === 'monthly_revenue' || key === 'monthly_claim_costs') formData.append(key, formatDecimalForSubmit(v[key]));
        else formData.append(key, v[key] ?? '');
      }
      formData.append('company_id', otk_company.id);
      postRequest(`/api/company_statistics/update/${formikUpdate.values.id}`, formData, true)
        .then(() => {
          getRequest('/api/company_statistics').then((response) => {
            dispatch({ type: 'load_company_statistics', newCompanyStatistics: response.data });
            if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
            setVisibleCompanyStatistics(false);
            formikUpdate.resetForm();
            toast?.current?.show({
              severity: 'info',
              summary: t('BusinessPartnerList.Toast.DeletedSummary'),
              life: 3000,
              detail: t('Common.Toast.RecordUpdatedDetail'),
            });
          });
          getRequest('/api/companies').then((response) => {
            dispatch({ type: 'load_companies', newCompanies: response.data });
          });
        })
        .catch(() => {
          if (!navigator.onLine) {
            alert(t('ServiceTask.OfflineQueuedAlert'));
          } else {
            alert(t('Common.Toast.ActionFailedDetail'));
          }
        });
    },
  });

  const formikUpdateRef = useRef(formikUpdate);
  formikUpdateRef.current = formikUpdate;

  const openEditFromRow = useCallback((rowData) => {
    const mapped = mapRowToUpdateFormValues(rowData);
    if (!mapped) return;
    setSelectedStatistics(rowData);
    formikUpdateRef.current.setValues(mapped);
    setVisibleCompanyStatistics(true);
  }, []);

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => openEditFromRow(rowData)}><EditRowIcon /></button>
      </div>
    );
  }

  const onSelectRow = useCallback((data) => {
    const rows = Array.isArray(data) ? data : [];
    const count = rows.length;
    const canEdit = user?.permissions?.includes('edit settings');
    const canAdd = user?.permissions?.includes('add settings');
    const canDelete = user?.permissions?.includes('delete settings');
    setEditIcon(!canEdit || count !== 1);
    setCopyIcon(!canAdd || count !== 1);
    setDeleteIcon(!canDelete || count < 1);
    setSelectedStatistics(count === 1 ? rows[0] : null);
  }, [user?.permissions]);

  const textHeaderFilter = useCallback((headerValue, rowValue) => {
    if (!headerValue) return true;
    return normalizeText(rowValue).includes(normalizeText(headerValue));
  }, []);

  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;

  const dateOrUserFilter = useCallback((headerValue, rowValue, rowData, filterParams) => {
    if (!headerValue) return true;
    const raw = String(headerValue || '').trim();
    if (raw.startsWith('range:')) {
      const p = raw.slice('range:'.length).split(':');
      if (p.length >= 2 && p[0] && p[1]) {
        const from = DateTime.fromFormat(p[0], 'yyyy-MM-dd').startOf('day');
        const to = DateTime.fromFormat(p[1], 'yyyy-MM-dd').endOf('day');
        const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
        return dt.isValid ? dt >= from && dt <= to : false;
      }
      return true;
    }
    const search = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const userObj = filterParams?.name === 'created_at' ? rowData?.contractor : rowData?.last_touched_by_user;
    const uname = userObj?.employee?.name || userObj?.username || '';
    const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
    const dateStr = dt.isValid ? `${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}` : '';
    const hay = `${uname} ${dateStr}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return hay.includes(search);
  }, [dateFmt, dateTimeFmt]);

  const currencySymbol = getCurrency(state.module_settings || []);

  function CreatedAtStatFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.contractor;
    const raw = rowData?.created_at;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw ?? '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={u?.employee?.name || u?.username}
            label={getUserInitials(u?.employee?.name || u?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function UpdatedAtStatFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.last_touched_by_user;
    const raw = rowData?.updated_at;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw || '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={u?.employee?.name || u?.username}
            label={getUserInitials(u?.employee?.name || u?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  const columns = useMemo(() => {
    const periodTitle = t('OtkCompanyStatisticsShared.Column.MonthInYear');
    const monthlyRevTitle = t('OtkCompanyStatisticsShared.Column.MonthlyRevenue').replace(/\{\{currency\}\}/g, currencySymbol);
    const claimTitle = t('OtkCompanyStatisticsShared.Column.MonthlyClaimCosts').replace(/\{\{currency\}\}/g, currencySymbol);
    const syncTitle = t('OtkCompanyStatisticsShared.Column.SyncClaimCostsFromDb');
    const ordersTitle = t('OtkCompanyStatisticsShared.Column.OrdersCount');
    const itemsTitle = t('OtkCompanyStatisticsShared.Column.ItemsCount');
    const createdTitle = t('EmployeeList.Column.CreatedAt');
    const updatedTitle = t('EmployeeList.Column.UpdatedAt');
    const yes = t('Common.Yes');
    const no = t('Common.No');
    return [
      { field: 'rowSelection', formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', width: 44, headerSort: false, resizable: false, frozen: true, print: false, download: false },
      { title: '', field: 'editor', hozAlign: 'center', width: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print: false, download: false },
      { title: t('ChecklistHolidayList.Column.Id'), field: 'id', minWidth: TABULATOR_LIST_ID_MIN_WIDTH, widthGrow: 0.5, hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
      { title: periodTitle, field: 'period', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(periodTitle), widthGrow: 1 },
      { title: monthlyRevTitle, field: 'monthly_revenue', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(monthlyRevTitle), widthGrow: 1, formatter: (cell) => formatCurrency(cell.getValue(), currencySymbol) },
      { title: claimTitle, field: 'monthly_claim_costs', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(claimTitle), widthGrow: 1, formatter: (cell) => formatCurrency(cell.getValue(), currencySymbol) },
      { title: syncTitle, field: 'sync_claim_costs', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: (hv, rv, rowData) => textHeaderFilter(hv, rowData?.sync_claim_costs ? yes : no), minWidth: tabulatorColumnMinWidthFromTitle(syncTitle), widthGrow: 1, formatter: (cell) => (cell.getValue() ? yes : no) },
      { title: ordersTitle, field: 'orders_count', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(ordersTitle), widthGrow: 1 },
      { title: itemsTitle, field: 'items_count', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(itemsTitle), widthGrow: 1 },
      { title: createdTitle, field: 'created_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(createdTitle), widthGrow: 1, formatter: reactFormatter(<CreatedAtStatFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'created_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, sorter: 'datetime', sorterParams: { format: 'iso' } },
      { title: updatedTitle, field: 'updated_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(updatedTitle), widthGrow: 1, formatter: reactFormatter(<UpdatedAtStatFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'updated_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, sorter: 'datetime', sorterParams: { format: 'iso' } },
    ];
  }, [currencySymbol, dateFmt, dateTimeFmt, dateOrUserFilter, openEditFromRow, textHeaderFilter, t]);

  const getMaskColumnLabel = useCallback((field) => ({
    id: t('ChecklistHolidayList.Column.Id'),
    period: t('OtkCompanyStatisticsShared.Column.MonthInYear'),
    monthly_revenue: t('OtkCompanyStatisticsShared.Column.MonthlyRevenue').replace(/\{\{currency\}\}/g, currencySymbol),
    monthly_claim_costs: t('OtkCompanyStatisticsShared.Column.MonthlyClaimCosts').replace(/\{\{currency\}\}/g, currencySymbol),
    sync_claim_costs: t('OtkCompanyStatisticsShared.Column.SyncClaimCostsFromDb'),
    orders_count: t('OtkCompanyStatisticsShared.Column.OrdersCount'),
    items_count: t('OtkCompanyStatisticsShared.Column.ItemsCount'),
    created_at: t('EmployeeList.Column.CreatedAt'),
    updated_at: t('EmployeeList.Column.UpdatedAt'),
  }[field] || field), [currencySymbol, t]);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE_STATS);
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    table.getColumns().forEach((col) => {
      const field = col.getField?.() ?? col.getDefinition?.()?.field;
      if (!field || !allowedSet.has(field)) return;
      if (columnsVisible[field] === false || seen.has(field)) return;
      seen.add(field);
      out.push(field);
    });
    return out.length > 0 ? out : fallback();
  }, [columnsVisible, getTable]);

  const parseHeaderFiltersToParams = useCallback(() => {
    const hf = getTable()?.getHeaderFilters?.() ?? [];
    const out = {};
    hf.forEach((f) => {
      if (!f?.field) return;
      const v = f.value;
      if (v == null || String(v).trim() === '') return;
      out[`filter_${f.field}`] = v;
    });
    return out;
  }, [getTable]);

  const buildStatisticsExportQueryString = useCallback((opts = {}) => {
    const params = {
      company_id: String(otk_company?.id ?? ''),
      ...parseHeaderFiltersToParams(),
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(getTable()?.getSorters?.() ?? [])),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [getTable, otk_company?.id, parseHeaderFiltersToParams]);

  const runStatisticsPrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildStatisticsExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`/api/company_statistics/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.PrintTitle'), detail: t('Common.EnablePopups'), life: 4000 });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('BusinessPartnerList.Toast.PrintTitle'), detail: e?.message || t('BusinessPartnerList.Toast.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildStatisticsExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportStatisticsXlsx = useCallback(async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildStatisticsExportQueryString({ includePrintColumns: true, printColumns: getPrintColumnsOrder() });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.COMPANY_STATISTICS_MONTHLY));
      const { blob, filename } = await downloadBlobGet(`/api/company_statistics/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('BusinessPartnerList.Toast.ExportTitle'), detail: e?.message || t('BusinessPartnerList.Toast.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildStatisticsExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, t, toast]);

  const closeMenu = () => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  };

  const saveUserMaskColumns = () => {
    const normalized = normalizeStatsColumnVisibilityForSave(columnsVisible);
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.MaskWarnTitle'), detail: t('BusinessPartnerList.Toast.SelectOneColumn'), life: 4000 });
      return;
    }
    setUserMaskFilter(PAGE_ID, 'all', normalized, getTable()?.getHeaderFilters?.() ?? [], null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('OtkCompanyStatisticsList.SaveStandardMaskMessage'),
      header: t('OtkCompanyStatisticsList.SaveStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const layout = getTabulatorLayoutForStandardMask(getTable());
        if (!layout) return;
        setStandardMaskFilter(PAGE_ID, columnsFromTabulatorForStandardMaskSave(layout.columns), layout.attributes);
      },
    });
  };

  const applyStandardLayout = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === PAGE_ID);
    if (!standard) return;
    const attributes = safeParse(standard.attributes, []);
    const jsonColumns = safeParse(standard.columns, {});
    const merged = mergeVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizeStatsColumnVisibilityForSave(merged);
    confirmDialog({
      message: t('OtkCompanyStatisticsList.ApplyStandardMaskMessage'),
      header: t('OtkCompanyStatisticsList.ApplyStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Apply'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter(PAGE_ID, 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          table.setColumnLayout?.(attributes);
          setColumnsVisible(colsForSave);
          applyDataColumnVisibilityToTable(table, colsForSave);
          setTimeout(() => { isRestoringRef.current = false; }, 0);
        });
      },
    });
  };

  function setMask(field, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE_STATS, field)) return;
    const next = { ...DEFAULT_COLUMNS_VISIBLE_STATS, ...columnsVisible, [field]: value };
    if (value === false && countVisibleDataColumns(next) < 1) {
      toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.MaskWarnTitle'), detail: t('BusinessPartnerList.Toast.AtLeastOneColumn'), life: 3500 });
      return;
    }
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function maskSelectAll() {
    const next = { ...DEFAULT_COLUMNS_VISIBLE_STATS };
    Object.keys(next).forEach((k) => { next[k] = true; });
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function maskDeselectAll() {
    const next = {};
    Object.keys(DEFAULT_COLUMNS_VISIBLE_STATS).forEach((k) => { next[k] = false; });
    next.period = true;
    next.id = true;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) setExpandedKeys({});
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  useEffect(() => {
    if (normalizeText(String(submittedSearchValueRef.current ?? '').trim()) === '') return;
    applyToolbarSearchRowFilter();
  }, [statisticsRows, applyToolbarSearchRowFilter]);

  const items = [{
    key: '0',
    label: t('Common.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.SetFilter'), icon: 'pi pi-filter', command: () => { setUserMaskFilter(PAGE_ID, 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); closeMenu(); }, template: itemRenderer },
      { key: '0_2', label: t('Common.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { setUserMaskFilter(PAGE_ID, 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); closeMenu(); }, template: itemRenderer },
      { key: '0_3', label: t('Common.SetSorting'), icon: 'pi pi-sort-alt', command: () => { setUserMaskFilter(PAGE_ID, 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); closeMenu(); }, template: itemRenderer },
      { key: '0_4', label: t('Common.ClearSorting'), icon: 'pi pi-sort-alt-slash', command: () => { setUserMaskFilter(PAGE_ID, 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); closeMenu(); }, template: itemRenderer },
      { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: () => { setMaskVisible(true); closeMenu(); }, template: itemRenderer },
      ...(hasApplicableStandardMask ? [{ key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: applyStandardLayout, template: itemRenderer }] : []),
      { key: '0_6', label: t('Common.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences(PAGE_ID); closeMenu(); }, template: itemRenderer },
    ],
  }];

  const addNewOtkCompanyStatistics = () => setShowOtkStatisticsAdd(true);

  const copyOtkCompanyStatistics = () => {
    const r = getTable()?.getSelectedData?.()?.[0];
    if (!r) return;
    formik.setValues({
      month: Number(r.month),
      year: Number(r.year),
      monthly_revenue: formatDecimalForDisplay(r.monthly_revenue),
      monthly_claim_costs: formatDecimalForDisplay(r.monthly_claim_costs),
      orders_count: r.orders_count ?? '',
      items_count: r.items_count ?? '',
      sync_claim_costs: !!r.sync_claim_costs,
    });
    setShowOtkStatisticsAdd(true);
  };

  const editNewOtkCompanyStatistics = () => {
    const r = getTable()?.getSelectedData?.()?.[0];
    if (r) openEditFromRow(r);
  };

  const hideEditOtkCompanyStatistics = () => {
    formikUpdate.resetForm();
    setVisibleCompanyStatistics(false);
  };

  const rejectDelete = () => {
    toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.RejectedSummary'), detail: t('ServiceTask.RequestCancelled'), life: 3000 });
  };

  const acceptDeleteStatistics = () => {
    const rows = getTable()?.getSelectedData?.() ?? [];
    if (rows.length === 0) return;
    Promise.all(rows.map((item) => deleteRequest(`/api/company_statistics/${item.id}`)))
      .then(() => {
        toast?.current?.show({ severity: 'info', summary: t('BusinessPartnerList.Toast.DeletedSummary'), life: 3000, detail: t('BusinessPartnerList.Toast.DeletedDetail') });
        return getRequest('/api/company_statistics');
      })
      .then((response) => {
        if (response?.data) dispatch({ type: 'load_company_statistics', newCompanyStatistics: response.data });
        return getRequest('/api/companies');
      })
      .then((response) => {
        if (response?.data) dispatch({ type: 'load_companies', newCompanies: response.data });
      })
      .catch(() => {
        toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.RejectedSummary'), detail: t('BusinessPartnerList.Toast.DeleteFailedDetail'), life: 3000 });
      });
  };

  const confirmDelete = () => {
    confirmDialog({
      message: t('EmployeeList.ConfirmDeleteMany'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: acceptDeleteStatistics,
      reject: rejectDelete,
    });
  };

  const searchTable = () => {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    applyToolbarSearchRowFilter();
    setVisibleSearch(false);
  };

  return (
    <>
      <ConfirmDialog />
      <div>
        <div className="w-full h-10 flex flex-row bg-[#333]">
          <IntersectionObserverWrapper>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={!user?.permissions?.includes('add settings')} onClick={addNewOtkCompanyStatistics}><AddIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={copyOtkCompanyStatistics}><CopyIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={editNewOtkCompanyStatistics}><EditIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={deleteIcon} onClick={confirmDelete}><DeleteIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : () => { setSearchIconColor(''); setSearchValue(''); submittedSearchValueRef.current = ''; applyToolbarSearchRowFilter(); }}>{searchIconColor === '' ? <MdSearch fill="#fff" className="w-[28px] h-[28px]" /> : <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" />}</button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={exportStatisticsXlsx}><ExportIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={reloadStatisticsAndCompanies} title={t('Common.Refresh')}><RefreshIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={statisticsRows.length === 0} onClick={scrollToFirstStat}><BackToFirstIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={statisticsRows.length === 0} onClick={scrollUpStat}><BackIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={statisticsRows.length === 0} onClick={scrollDownStat}><ForwardIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={statisticsRows.length === 0} onClick={scrollToLastStat}><ForwardToLastIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => openFavoriteDialog(t('OtkCompanyStatisticsList.Tab.EditBusinessData'), PAGE_ID)}><FavoriteIcon /></button></div>
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => setFastView(PAGE_ID, columnsVisible, columns)}><PriceIcon /></button></div>
            {canSetStandardMask ? (
              <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
                <button type="button" title={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask}>
                  <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
                </button>
              </div>
            ) : null}
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
            <div className="card flex justify-content-center z-[100]" ref={overlayRef}>
              <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
            </div>
          </IntersectionObserverWrapper>
        </div>

        <Splitter className="h-[calc(100vh_-_177px)]" layout="vertical" pt={{ gutter: { className: 'bg-[#333] h-8' }, gutterHandler: { className: 'bg-secondary ' } }}>
          <SplitterPanel className="flex align-items-center justify-content-center overflow-y-auto" minSize={10} size={63}>
            <div className="w-full relative flex flex-col h-full overflow-x-auto">
              <div className="xl:grid grid-cols-2 gap-4 mb-2">
                <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md space-y-3">
                  <h1 className="text-3xl font-semibold text-left text-zinc-900">{t('OtkCompanyStatisticsAdd.SectionDocsTitle')}</h1>
                  {formType === 'edit' && (
                    <p className="block text-md font-semibold text-gray-800" id="otk-stat-company-id-label">{t('Common.RecordIdLabel')}: {otk_company?.id}</p>
                  )}
                  <div className="space-y-1">
                    <label htmlFor="otk-stat-readonly-name" className="block text-md font-semibold text-gray-800">{t('OtkCompanyStatisticsAdd.LabelCompanyName')}</label>
                    <input id="otk-stat-readonly-name" readOnly name="name" type="text" value={otk_company?.name ?? ''} aria-readonly="true" className="block w-full px-4 py-2 mt-1 text-grey-700 bg-gray-200 border rounded-md" />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="otk-stat-readonly-tel" className="block text-md font-semibold text-gray-800">{t('OtkCompanyStatisticsAdd.LabelPhone')}</label>
                    <input id="otk-stat-readonly-tel" readOnly name="tel_number" type="text" value={otk_company?.tel_number ?? ''} aria-readonly="true" className="block w-full px-4 py-2 mt-1 text-grey-700 bg-gray-200 border rounded-md" />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="otk-stat-readonly-email" className="block text-md font-semibold text-gray-800">{t('OtkCompanyStatisticsAdd.LabelEmail')}</label>
                    <input id="otk-stat-readonly-email" readOnly name="email" type="text" value={otk_company?.email ?? ''} aria-readonly="true" className="block w-full px-4 py-2 mt-1 text-grey-700 bg-gray-200 border rounded-md" />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="otk-stat-readonly-contact" className="block text-md font-semibold text-gray-800">{t('OtkCompanyStatisticsAdd.LabelContactPerson')}</label>
                    <input id="otk-stat-readonly-contact" readOnly name="contact_person" type="text" value={otk_company?.contact_person ?? ''} aria-readonly="true" className="block w-full px-4 py-2 mt-1 text-grey-700 bg-gray-200 border rounded-md" />
                  </div>
                </div>
                <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md space-y-3">
                  <div className="space-y-1">
                    <label htmlFor="otk-stat-readonly-address" className="block text-md font-semibold text-gray-800">{t('OtkCompanyStatisticsAdd.LabelAddress')}</label>
                    <input id="otk-stat-readonly-address" readOnly name="address" type="text" value={otk_company?.address ?? ''} aria-readonly="true" className="block w-full px-4 py-2 mt-1 text-grey-700 bg-gray-200 border rounded-md" />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="otk-stat-readonly-fax" className="block text-md font-semibold text-gray-800">{t('OtkCompanyStatisticsAdd.LabelFax')}</label>
                    <input id="otk-stat-readonly-fax" readOnly name="fax" type="text" value={otk_company?.fax ?? ''} aria-readonly="true" className="block w-full px-4 py-2 mt-1 text-grey-700 bg-gray-200 border rounded-md" />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="otk-stat-readonly-web" className="block text-md font-semibold text-gray-800">{t('OtkCompanyStatisticsAdd.LabelWeb')}</label>
                    <input id="otk-stat-readonly-web" readOnly name="web_address" type="text" value={otk_company?.web_address ?? ''} aria-readonly="true" className="block w-full px-4 py-2 mt-1 text-grey-700 bg-gray-200 border rounded-md" />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="otk-stat-readonly-desc" className="block text-md font-semibold text-gray-800">{t('OtkCompanyStatisticsAdd.LabelDescription')}</label>
                    <textarea id="otk-stat-readonly-desc" readOnly name="description" value={otk_company?.description ?? ''} aria-readonly="true" rows={4} className="block w-full px-4 py-2 mt-1 text-grey-700 bg-gray-200 border rounded-md resize-y" />
                  </div>
                </div>
              </div>
            </div>
          </SplitterPanel>
          <SplitterPanel className="flex flex-col align-items-center justify-content-center w-full overflow-y-auto" size={37} style={{ minHeight: '110px' }}>
            <ReactTabulator
              data={statisticsRows}
              className="h-[calc(100vh_-_176px)]"
              onRef={(r) => { ref.current = r?.current; }}
              columns={columns}
              events={{
                rowSelectionChanged: onSelectRow,
                tableBuilt: () => {
                  const table = getTable();
                  if (!table) return;
                  const effective = getEffectiveMask(PAGE_ID, state);
                  if (effective?.type === 'user' && effective.userMask) {
                    isRestoringRef.current = true;
                    applyUserMaskRowToTabulator(table, effective.userMask, {
                      setColumnsVisible: (v) => setColumnsVisible(mergeVisibility(v)),
                      clearHeaderFiltersWhenEmpty: true,
                      applyColumnVisibilityFn: (tbl, jc, setVis) => {
                        const merged = mergeVisibility(jc);
                        setVis(merged);
                        applyDataColumnVisibilityToTable(tbl, merged);
                      },
                    });
                    setTimeout(() => { isRestoringRef.current = false; }, 0);
                  } else if (effective?.type === 'standard' && effective.standardMask) {
                    isRestoringRef.current = true;
                    const attributes = safeParse(effective.standardMask.attributes, []);
                    const jsonColumns = safeParse(effective.standardMask.columns, {});
                    if (Array.isArray(attributes) && attributes.length > 1) table.setColumnLayout?.(attributes);
                    if (jsonColumns && typeof jsonColumns === 'object') {
                      const merged = mergeVisibility(jsonColumns);
                      setColumnsVisible(merged);
                      applyDataColumnVisibilityToTable(table, merged);
                    }
                    setTimeout(() => { isRestoringRef.current = false; }, 0);
                  } else {
                    applyDataColumnVisibilityToTable(table, DEFAULT_COLUMNS_VISIBLE_STATS);
                  }
                  setTimeout(() => applyToolbarSearchRowFilter(), 0);
                },
                ...getTabulatorPersistLayoutEvents(PAGE_ID, ref, setUserMaskFilter),
              }}
              options={{ renderHorizontal: 'virtual', layout: 'fitColumns', movableColumns: true, autoResize: true, headerSortTristate: true, filterMode: 'local' }}
            />
          </SplitterPanel>
        </Splitter>
      </div>

      <PrintOptionsDialog visible={printChoiceVisible} onHide={() => setPrintChoiceVisible(false)} orientation={printOrientationChoice} onOrientationChange={setPrintOrientationChoice} layout={printLayoutChoice} onLayoutChange={setPrintLayoutChoice} onConfirm={runStatisticsPrint} />

      <Dialog header={t('BusinessPartnerList.SearchDialogTitle')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-col">
          <input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} type="text" className="block w-full px-4 py-2 border rounded-md" />
          <button type="button" onClick={searchTable} className="w-16 mt-3 px-4 py-2 text-white bg-zinc-900 rounded-md"><SearchIcon /></button>
        </div>
      </Dialog>

      <Dialog header={t('OtkCompanyStatisticsList.Tab.EditBusinessData')} visible={showOtkStatisticsAdd} style={{ width: '40vw' }} onHide={() => setShowOtkStatisticsAdd(false)}>
        <form onSubmit={formik.handleSubmit}>
          <div>
            <div>
              <label htmlFor="period-create" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.MonthInYear')}</label>
              <div className="flex gap-2 items-center text-black mt-1">
                <Dropdown inputId="period-create" value={formik.values.month} options={months} optionValue="val" onChange={(e) => formik.setFieldValue('month', e.value)} optionLabel="label" placeholder={t('OtkCompanyStatisticsShared.Form.PlaceholderMonth')} required />
                <Dropdown value={formik.values.year} options={years} optionValue="val" onChange={(e) => formik.setFieldValue('year', e.value)} optionLabel="label" placeholder={t('OtkCompanyStatisticsShared.Form.PlaceholderYear')} required />
              </div>
            </div>
            <div>
              <label htmlFor="monthly_revenue_c" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.MonthlyRevenue').replace(/\{\{currency\}\}/g, currencySymbol)}</label>
              <input id="monthly_revenue_c" name="monthly_revenue" type="text" required onChange={formik.handleChange} value={formik.values.monthly_revenue} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
            <div>
              <div className="flex items-center gap-2 mt-3">
                <Checkbox inputId="sync_claim_costs_create" checked={formik.values.sync_claim_costs} onChange={(e) => formik.setFieldValue('sync_claim_costs', e.checked)} />
                <label htmlFor="sync_claim_costs_create" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.SyncClaimsFromDb')}</label>
              </div>
              {!formik.values.sync_claim_costs ? (
                <>
                  <label htmlFor="monthly_claim_costs_c" className="font-bold text-black mt-2">{t('OtkCompanyStatisticsShared.Form.ClaimCostsMonth').replace(/\{\{currency\}\}/g, currencySymbol)}</label>
                  <input id="monthly_claim_costs_c" name="monthly_claim_costs" type="text" required onChange={formik.handleChange} value={formik.values.monthly_claim_costs} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
                </>
              ) : (
                <small className="block mt-2 text-gray-600">{t('OtkCompanyStatisticsShared.Form.ClaimCostsAutoHint')}</small>
              )}
            </div>
            <div>
              <label htmlFor="orders_c" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.OrdersPerMonth')}</label>
              <input id="orders_c" name="orders_count" type="text" required onChange={formik.handleChange} value={formik.values.orders_count} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
            <div>
              <label htmlFor="items_c" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.ItemsPerMonth')}</label>
              <input id="items_c" name="items_count" type="text" required onChange={formik.handleChange} value={formik.values.items_count} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
          </div>
          <button type="submit" className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25">{t('OtkCompanyStatisticsShared.Form.SaveState')}</button>
        </form>
      </Dialog>

      <Dialog header={t('OtkCompanyStatisticsShared.Dialog.EditBusinessDataTitle')} visible={visibleCompanyStatistics} style={{ width: '40vw' }} onHide={() => hideEditOtkCompanyStatistics()}>
        <form onSubmit={formikUpdate.handleSubmit}>
          <div>
            <div>
              <label htmlFor="period-edit" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.MonthInYear')}</label>
              <div className="flex gap-2 items-center text-black mt-1">
                <Dropdown inputId="period-edit" value={formikUpdate.values.month} options={months} optionValue="val" onChange={(e) => formikUpdate.setFieldValue('month', e.value)} optionLabel="label" placeholder={t('OtkCompanyStatisticsShared.Form.PlaceholderMonth')} required />
                <Dropdown value={formikUpdate.values.year} options={years} optionValue="val" onChange={(e) => formikUpdate.setFieldValue('year', e.value)} optionLabel="label" placeholder={t('OtkCompanyStatisticsShared.Form.PlaceholderYear')} required />
              </div>
            </div>
            <div>
              <label htmlFor="monthly_revenue_e" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.MonthlyRevenue').replace(/\{\{currency\}\}/g, currencySymbol)}</label>
              <input id="monthly_revenue_e" name="monthly_revenue" type="text" required onChange={formikUpdate.handleChange} value={formikUpdate.values.monthly_revenue} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
            <div>
              <div className="flex items-center gap-2 mt-3">
                <Checkbox inputId="sync_claim_costs_update" checked={formikUpdate.values.sync_claim_costs} onChange={(e) => formikUpdate.setFieldValue('sync_claim_costs', e.checked)} />
                <label htmlFor="sync_claim_costs_update" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.SyncClaimsFromDb')}</label>
              </div>
              {!formikUpdate.values.sync_claim_costs ? (
                <>
                  <label htmlFor="monthly_claim_costs_e" className="font-bold text-black mt-2">{t('OtkCompanyStatisticsShared.Form.ClaimCostsMonth').replace(/\{\{currency\}\}/g, currencySymbol)}</label>
                  <input id="monthly_claim_costs_e" name="monthly_claim_costs" type="text" required onChange={formikUpdate.handleChange} value={formikUpdate.values.monthly_claim_costs} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
                </>
              ) : (
                <small className="block mt-2 text-gray-600">{t('OtkCompanyStatisticsShared.Form.ClaimCostsAutoHint')}</small>
              )}
            </div>
            <div>
              <label htmlFor="orders_e" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.OrdersPerMonth')}</label>
              <input id="orders_e" name="orders_count" type="text" required onChange={formikUpdate.handleChange} value={formikUpdate.values.orders_count} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
            <div>
              <label htmlFor="items_e" className="font-bold text-black">{t('OtkCompanyStatisticsShared.Form.ItemsPerMonth')}</label>
              <input id="items_e" name="items_count" type="text" required onChange={formikUpdate.handleChange} value={formikUpdate.values.items_count} className="block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md" />
            </div>
          </div>
          <button type="submit" className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25">{t('OtkCompanyStatisticsShared.Form.SaveState')}</button>
        </form>
      </Dialog>

      <Dialog header={t('OtkCompanyStatisticsPeriodList.MaskDialogTitle')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2 m-1">
            <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('OtkCompanyStatisticsPeriodList.MaskSelectAll')}</button>
            <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('OtkCompanyStatisticsPeriodList.MaskDeselectAll')}</button>
          </div>
          {Object.keys(DEFAULT_COLUMNS_VISIBLE_STATS).map((field) => (
            <div key={field} className="flex flex-row border border-black rounded-md p-2 m-1">
              <Checkbox inputId={`otk-stat-add-mask-${field}`} onChange={(e) => setMask(field, e.checked)} checked={columnsVisible[field] !== false} />
              <label htmlFor={`otk-stat-add-mask-${field}`} className="ml-2 cursor-pointer">{getMaskColumnLabel(field)}</label>
            </div>
          ))}
          <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 text-center tracking-wide text-white bg-zinc-900 rounded-md hover:bg-zinc-700">{t('OtkCompanyStatisticsPeriodList.MaskSave')}</button>
        </div>
      </Dialog>
    </>
  );
}
